import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Check, Loader2, CreditCard, Shield, ArrowRight, Star, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES = {
  coalition_annual: {
    name: 'Coalition Annual',
    price: '$888',
    period: '/year',
    description: 'Best Value! All access + Brand Builder included',
    featured: true,
    badge: 'BEST VALUE',
    features: [
      'Full coalition access',
      'Target List (all 12 states)',
      'Permissioned introductions',
      'Outcome tracking dashboard',
      'Priority support',
      'Brand Builder access included',
      'AI Blueprint Generator included'
    ]
  },
  coalition_monthly_setup: {
    name: 'Coalition Monthly',
    price: '$499',
    period: 'setup + $99/mo',
    description: 'Flexible monthly membership',
    features: [
      'Full coalition access',
      'Target List (all 12 states)',
      'Permissioned introductions',
      'Outcome tracking dashboard',
      'Email + chat support',
      '$99/month after setup'
    ]
  },
  brand_builder: {
    name: 'Brand Builder',
    price: '$399',
    period: 'one-time',
    description: 'Standalone tools access',
    features: [
      'Source-to-Shelf Brand Builder',
      'AI Blueprint Generator',
      'Manufacturer recommendations',
      'Packaging supplier database',
      'Lifetime access',
      'No coalition access'
    ]
  }
};

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const packageId = searchParams.get('package') || 'coalition_annual';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(
    PACKAGES[packageId] ? packageId : 'coalition_annual'
  );

  const currentPackage = PACKAGES[selectedPackage];

  async function initiatePayment() {
    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: selectedPackage,
          origin_url: originUrl
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message || 'Payment error');
      setLoading(false);
    }
  }

  // Calculate display price
  const getDisplayPrice = () => {
    if (selectedPackage === 'coalition_monthly_setup') {
      return { main: '$499', sub: 'then $99/mo' };
    }
    return { main: currentPackage.price, sub: currentPackage.period };
  };

  const displayPrice = getDisplayPrice();

  return (
    <Layout>
      <section className="section-padding bg-slate-50 min-h-screen" data-testid="checkout-page">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-3">
                Join BevRelay
              </h1>
              <p className="text-slate-600">
                Choose your membership and start connecting with buyers
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Package Selection */}
              <div className="md:col-span-3 space-y-4">
                <h2 className="font-semibold text-navy mb-4">Select Your Plan</h2>
                
                {Object.entries(PACKAGES).map(([id, pkg]) => (
                  <button
                    key={id}
                    onClick={() => setSelectedPackage(id)}
                    className={`w-full p-5 rounded-xl text-left transition-all ${
                      selectedPackage === id 
                        ? 'bg-white border-2 border-copper shadow-md' 
                        : 'bg-white border border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`package-${id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-navy">{pkg.name}</span>
                          {pkg.badge && (
                            <span className="text-xs bg-copper text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              {pkg.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <span className="font-heading text-2xl font-bold text-navy">{pkg.price}</span>
                        <div className="text-slate-500 text-sm">{pkg.period}</div>
                      </div>
                    </div>
                    
                    {selectedPackage === id && (
                      <ul className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-copper flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                ))}
                
                {/* Monthly recurring note */}
                {selectedPackage === 'coalition_monthly_setup' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                    <strong>Note:</strong> After the $499 setup, you'll be billed $99/month. 
                    Cancel anytime. Or choose Annual ($888) for best value.
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="md:col-span-2">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
                  <h2 className="font-semibold text-navy mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 pb-4 border-b border-slate-200">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{currentPackage.name}</span>
                      <span className="font-semibold text-navy">{currentPackage.price}</span>
                    </div>
                    {selectedPackage === 'coalition_monthly_setup' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Then monthly</span>
                        <span className="text-slate-600">$99/mo</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Billing</span>
                      <span className="text-slate-600 capitalize">
                        {selectedPackage === 'coalition_annual' ? 'Annual' : 
                         selectedPackage === 'coalition_monthly_setup' ? 'Setup fee' : 'One-time'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between py-4 border-b border-slate-200">
                    <span className="font-semibold text-navy">Due Today</span>
                    <span className="font-heading text-xl font-bold text-navy">{currentPackage.price}</span>
                  </div>

                  {selectedPackage === 'coalition_annual' && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                      <Zap className="w-4 h-4" />
                      <span>Save $699 vs monthly ($499 + $99×12)</span>
                    </div>
                  )}

                  {error && (
                    <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    onClick={initiatePayment}
                    disabled={loading}
                    className="w-full mt-6 py-4 px-6 rounded-xl bg-copper text-white font-semibold hover:bg-copper/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Checkout with Stripe
                      </>
                    )}
                  </button>
                  
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                    <Shield className="w-4 h-4" />
                    Secure payment via Stripe
                  </div>
                  
                  <p className="mt-4 text-xs text-slate-400 text-center">
                    By purchasing, you agree to our Terms of Service
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
